import { useState, useCallback, useEffect, useRef } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Shared hook for OAuth popup connect flow.
 * Used by both CredentialManager and NodeConfigPanel for inline OAuth.
 *
 * @param {Object} options
 * @param {Function} options.onSuccess - called with credential_id after successful connect
 * @param {Function} [options.onError] - called with error message on failure
 * @returns {{ connect, connecting, error, oauthProviders, fetchProviders }}
 */
export default function useOAuthConnect({ onSuccess, onError } = {}) {
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [oauthProviders, setOauthProviders] = useState([]);
  const cleanupRef = useRef(null);

  const fetchProviders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/oauth/providers`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setOauthProviders(data);
        return data;
      }
    } catch (_) {
      // Non-fatal
    }
    return [];
  }, []);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupRef.current) cleanupRef.current();
    };
  }, []);

  const connect = useCallback(async (provider, credName, credentialId) => {
    const name = credName || `My ${provider.charAt(0).toUpperCase() + provider.slice(1)} Account`;
    setConnecting(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      let authorizeUrl = `${API_BASE_URL}/oauth/authorize/${provider}?name=${encodeURIComponent(name)}`;
      if (credentialId) authorizeUrl += `&credential_id=${encodeURIComponent(credentialId)}`;
      const res = await fetch(
        authorizeUrl,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to start OAuth flow');
      }
      const { url } = await res.json();

      const popup = window.open(url, 'oauth_popup', 'width=600,height=700,scrollbars=yes,resizable=yes');
      if (!popup) {
        throw new Error('Popup was blocked. Please allow popups for this site and try again.');
      }

      const messageHandler = (event) => {
        if (!event.data || event.data.type !== 'oauth_callback') return;
        window.removeEventListener('message', messageHandler);
        setConnecting(false);
        cleanupRef.current = null;
        if (event.data.success) {
          setError(null);
          if (onSuccess) onSuccess(event.data.credential_id);
        } else {
          const errMsg = `OAuth failed: ${event.data.message}`;
          setError(errMsg);
          if (onError) onError(errMsg);
        }
      };
      window.addEventListener('message', messageHandler);

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          setConnecting(false);
          cleanupRef.current = null;
        }
      }, 1000);

      cleanupRef.current = () => {
        clearInterval(checkClosed);
        window.removeEventListener('message', messageHandler);
      };
    } catch (err) {
      setError(err.message);
      setConnecting(false);
      if (onError) onError(err.message);
    }
  }, [onSuccess, onError]);

  return { connect, connecting, error, oauthProviders, fetchProviders };
}
